import { createContext, useCallback, useEffect, useState, useRef } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { toast } from 'react-toastify'
import { toLower } from 'lodash-es'

import { useGetJobStatusQuery } from 'app/services/api/jobs'
import { downloadFileFromJob } from 'common/utils/exportUtils'

import {
  IJobExportStatus,
  CreateDataExportJob,
  IExportJobContext
} from './types'

const noJob = { jobId: '' }

interface IJobResult {
  jobId: string
  queuePosition: number
}

export const ExportJobContext = createContext<IExportJobContext>({
  exportJob: {},
  jobStatus: '',
  isJobPending: false,
  createDataExportJob: Promise.resolve
})

export const ExportJobProvider = ({ children }: React.PropsWithChildren) => {
  const [{ jobId, fileName, extension }, setJob] =
    useState<IJobExportStatus>(noJob)

  const exportJob = useGetJobStatusQuery(jobId || skipToken)

  const { currentData, refetch, isUninitialized } = exportJob
  const jobStatus = toLower(currentData?.jobStatus)
  const isJobPending = ['processing', 'pending'].includes(
    jobStatus.toLocaleLowerCase()
  )

  const intervalID = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (jobId && fileName && jobStatus) {
      const _onSuccess = (succesText: string) => {
        toast.success(succesText)
      }

      const _onFail = (failText: string) => {
        toast.error(failText)
      }

      const _stopPolling = () => {
        clearInterval(intervalID.current)
        intervalID.current = undefined
        setJob(noJob)
      }

      // not an ideal way to check for the status change from the server
      // server should create a stream or dispatch a socket
      if (
        !isUninitialized &&
        isJobPending &&
        intervalID.current === undefined
      ) {
        intervalID.current = setInterval(() => {
          refetch().catch(() => {
            _stopPolling()
            _onFail('Export unsuccessful')
          })
        }, 5555)
      } else if (jobStatus === 'success') {
        _stopPolling()

        downloadFileFromJob(jobId, fileName, extension)
          .then(_onSuccess)
          .catch(_onFail)
      } else if (jobStatus === 'rejected' || jobStatus === 'error') {
        _stopPolling()
        _onFail('Export unsuccessful')
      }
    }
  }, [
    isUninitialized,
    jobStatus,
    jobId,
    fileName,
    extension,
    isJobPending,
    refetch
  ])

  const createDataExportJob = useCallback<CreateDataExportJob>(
    (mutationTrigger, mutationArgs, fileName, extension = '.zip') =>
      mutationTrigger(mutationArgs)
        .unwrap()
        .then((jobResult: IJobResult) => {
          setJob({
            ...jobResult,
            fileName: fileName || `Export-${jobResult.jobId}`,
            extension
          })
        })
        .catch(() => {
          setJob(noJob)
        }),
    []
  )

  return (
    <ExportJobContext.Provider
      value={{ exportJob, jobStatus, isJobPending, createDataExportJob }}
    >
      {children}
    </ExportJobContext.Provider>
  )
}
